import {
  ConditionalFunctionString,
  PERCENTAGE_UNIT
} from '../../conditions.types'
import type { SemanticType } from '../../../semanticTypes'
import { parseAmount, parsePercentageOfAmount } from '../runtimeHelpers'
import { semanticTypeRuntimePath } from './utils'

/**
 * Configuration for the amount comparison factories. Operators declare which
 * upstream semantic types they need up front, so the factory can convert each
 * one to a runtime variable path internally. The switch task's input
 * parameters are keyed by semantic type, so these references resolve at
 * evaluation time.
 */
export type AmountFunctionStringConfig = {
  /**
   * Semantic type of the upstream value the percentage is calculated against.
   * Required when the operator allows the PERCENTAGE unit (e.g. discount as a
   * percentage of contract value uses
   * `percentageBaseSemanticType: semanticTypes.sequence.quote.totalContractValue`).
   */
  percentageBaseSemanticType?: SemanticType
  /**
   * Semantic type of the upstream value holding the comparison's currency
   * code. When set AND the user picks a currency unit, the runtime comparison
   * also asserts equality between the chosen unit and this value — e.g.
   * `parseAmount(...) > 100 && $["sequence.quote.currency"] === "USD"`.
   * Skipped when the user picked PERCENTAGE or hasn't picked a unit at all.
   */
  currencySemanticType?: SemanticType
}

type AmountWithUnitValue = {
  amount: string | number
  unit?: string
}

const readAmountValue = (value: unknown): AmountWithUnitValue => {
  if (typeof value === 'object' && value !== null && 'amount' in value) {
    return value as AmountWithUnitValue
  }
  // Fall back to treating a primitive as the bare amount with no unit — keeps
  // legacy data working.
  return { amount: value as string | number, unit: undefined }
}

/**
 * For unit-aware amount comparisons:
 *  - PERCENTAGE unit → `(property / percentageBase) * 100 <op> value`
 *  - currency unit (and `currencySemanticType` configured) →
 *    `parseAmount(property) <op> value && $["sequence.…"] === unit`
 *  - no unit (or no `currencySemanticType` configured) →
 *    `parseAmount(property) <op> value`
 */
const buildAmountComparison = ({
  property,
  amount,
  unit,
  operatorName,
  operator,
  percentageBaseSemanticType,
  currencySemanticType
}: AmountFunctionStringConfig & {
  property: string
  amount: string | number
  unit: string | undefined
  operatorName: string
  operator: '<' | '>' | '==='
}): string => {
  if (unit === PERCENTAGE_UNIT) {
    if (!percentageBaseSemanticType) {
      throw new Error(
        `${operatorName} with ${PERCENTAGE_UNIT} unit requires a "percentageBaseSemanticType" — declare it in the operator's factory config.`
      )
    }
    const percentageBasePath = semanticTypeRuntimePath(
      percentageBaseSemanticType
    )
    return `${parsePercentageOfAmount(property, percentageBasePath)} ${operator} ${amount}`
  }

  const baseComparison = `${parseAmount(property)} ${operator} ${amount}`

  if (!unit || !currencySemanticType) return baseComparison

  const currencyPath = semanticTypeRuntimePath(currencySemanticType)
  return `${baseComparison} && ${currencyPath} === ${JSON.stringify(unit)}`
}

export const lessThanAmount =
  (config: AmountFunctionStringConfig = {}): ConditionalFunctionString =>
  ({ property, value }) => {
    const { amount, unit } = readAmountValue(value)
    return buildAmountComparison({
      ...config,
      property,
      amount,
      unit,
      operatorName: 'lessThanAmount',
      operator: '<'
    })
  }

export const moreThanAmount =
  (config: AmountFunctionStringConfig = {}): ConditionalFunctionString =>
  ({ property, value }) => {
    const { amount, unit } = readAmountValue(value)
    return buildAmountComparison({
      ...config,
      property,
      amount,
      unit,
      operatorName: 'moreThanAmount',
      operator: '>'
    })
  }

export const equalsAmount =
  (config: AmountFunctionStringConfig = {}): ConditionalFunctionString =>
  ({ property, value }) => {
    const { amount, unit } = readAmountValue(value)
    return buildAmountComparison({
      ...config,
      property,
      amount,
      unit,
      operatorName: 'equalsAmount',
      operator: '==='
    })
  }
